import { cn } from '@/lib/utils';
import type { LeaderboardCopy } from '../leaderboard-copy';
import { publicName } from '../leaderboard-ranking';
import type { LeaderboardEntry } from '../leaderboard.types';
import { ContributorAvatar } from './ContributorAvatar';

interface RowProps {
  copy: LeaderboardCopy;
  entry: LeaderboardEntry;
  mine: boolean;
  format: (value: number) => string;
  languageName: (code: string) => string;
}

/** The person's languages as small chips (names, most used first). */
function Languages({ codes, languageName }: { codes: string[]; languageName: (code: string) => string }) {
  if (codes.length === 0) return <span className="text-xs text-faint-foreground">-</span>;
  return (
    <span className="flex flex-wrap gap-1">
      {codes.map((code) => (
        <span key={code} className="rounded-full bg-surface-2 px-2 py-0.5 text-xs text-muted-foreground">
          {languageName(code)}
        </span>
      ))}
    </span>
  );
}

function NameCell({ copy, entry, mine }: Pick<RowProps, 'copy' | 'entry' | 'mine'>) {
  const name = publicName(entry);
  return (
    <div className="flex min-w-0 items-center gap-3">
      <ContributorAvatar name={name} />
      <div className="min-w-0">
        <p className="flex items-center gap-1.5 text-sm font-medium text-foreground">
          <span className="truncate">{name}</span>
          {mine ? <span className="shrink-0 text-xs font-medium text-brand">{copy.you}</span> : null}
        </p>
        {entry.username && entry.username !== name ? (
          <p className="truncate text-xs text-faint-foreground">@{entry.username}</p>
        ) : null}
      </div>
    </div>
  );
}

/** One table row from md up, 44px high. */
export function LeaderboardTableRow({ copy, entry, mine, format, languageName }: RowProps) {
  return (
    <tr className={cn('h-11 transition-colors', mine ? 'bg-brand/[0.06]' : 'hover:bg-surface-2/60')} aria-current={mine ? 'true' : undefined}>
      <td className="w-16 px-4 text-sm tabular-nums text-muted-foreground">{entry.rank}</td>
      <td className="max-w-0 px-4 py-1.5">
        <NameCell copy={copy} entry={entry} mine={mine} />
      </td>
      <td className="w-56 px-4 py-1.5">
        <Languages codes={entry.languages} languageName={languageName} />
      </td>
      <td className="w-36 px-4 text-right text-sm font-semibold tabular-nums text-foreground">{format(entry.contributions)}</td>
    </tr>
  );
}

/** The same line as a card, under md. */
export function LeaderboardCard({ copy, entry, mine, format, languageName }: RowProps) {
  return (
    <li
      className={cn('flex min-h-11 items-center gap-3 px-4 py-2.5', mine && 'bg-brand/[0.06]')}
      aria-current={mine ? 'true' : undefined}
    >
      <span className="w-7 shrink-0 text-sm tabular-nums text-muted-foreground">{entry.rank}</span>
      <div className="min-w-0 flex-1 space-y-1">
        <NameCell copy={copy} entry={entry} mine={mine} />
        {entry.languages.length > 0 ? (
          <div className="pl-11">
            <Languages codes={entry.languages} languageName={languageName} />
          </div>
        ) : null}
      </div>
      <span className="shrink-0 text-sm font-semibold tabular-nums text-foreground">{format(entry.contributions)}</span>
    </li>
  );
}
